import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo } from 'react';
import { ProductCard } from '../components/ProductCard';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { ALL_PRODUCTS } from '../data/products';

const CATEGORIES = [
  { key: 'all', label: 'All', count: ALL_PRODUCTS.length },
  { key: 'beanies', label: 'Beanies', types: ['Beanie'] },
  { key: 'dreadlock', label: 'Dreadlock Hats', types: ['Dreadlock Hat', 'Dreadlock Tam'] },
  { key: 'premium', label: 'Premium', types: ['Premium Hat', 'Shawl'] },
  { key: 'accessories', label: 'Accessories', types: ['Scarf', 'Sun Hat', 'Rug'] },
];

// Pre-compute counts
CATEGORIES.forEach(cat => {
  if (cat.key !== 'all') {
    cat.count = ALL_PRODUCTS.filter(p => cat.types.includes(p.type)).length;
  }
});

export default function Products() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [formData, setFormData] = useState({
    hatType: '',
    description: '',
    email: '',
    name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleCustomOrderSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Note: Replace 'YOUR_FORMSPREE_ID' with actual Formspree ID
    try {
      const response = await fetch('https://formspree.io/f/meerbgdl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Custom order request sent! We\'ll contact you soon.');
        setFormData({ hatType: '', description: '', email: '', name: '' });
      } else {
        toast.error('Failed to send request. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div data-testid="products-page">
      {/* Page Header */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6"
          >
            Shop Handcrafted Crocheted Hats
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-[#5C5855] max-w-3xl mx-auto mb-4"
          >
            Browse all 16 handcrafted items by Christian Clark. Our collection includes beanie hats, dreadlock hats and slouchy tams, premium designs, infinity scarves, the signature Aurora Chevron Shawl, and the new Boho Rug. Prices range from $30 to $100.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-sm text-[#5C5855] max-w-2xl mx-auto"
          >
            All items are hand-crocheted with soft acrylic and cotton-blend yarns in Sacramento, CA. Ships nationwide via USPS in 3-5 business days. Don't see what you want? Submit a custom order below.
          </motion.p>
        </div>
      </section>
      
      {/* Category Filter + Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="category-filter">
            {CATEGORIES.map(cat => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-5 py-2.5 rounded-full font-[Manrope] text-sm font-semibold transition-all ${
                  activeCategory === cat.key
                    ? 'bg-[#A85A43] text-white shadow-md'
                    : 'bg-white text-[#5C5855] border border-[#EADCCC] hover:border-[#A85A43] hover:text-[#A85A43]'
                }`}
                data-testid={`filter-${cat.key}`}
              >
                {cat.label}
                <span className={`ml-1.5 text-xs ${activeCategory === cat.key ? 'text-white/70' : 'text-[#5C5855]/50'}`}>
                  ({cat.count})
                </span>
              </button>
            ))}
          </div>

          {/* Filtered Products Grid */}
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12"
          >
            <AnimatePresence mode="popLayout">
              {ALL_PRODUCTS
                .filter(product => {
                  if (activeCategory === 'all') return true;
                  const cat = CATEGORIES.find(c => c.key === activeCategory);
                  return cat?.types?.includes(product.type);
                })
                .map((product, index) => (
                  <motion.div
                    key={product.name}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <ProductCard {...product} index={index} />
                  </motion.div>
                ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      
      {/* Custom Order Section */}
      <section className="py-24 bg-gradient-to-br from-[#A85A43]/10 to-[#7A8974]/10" data-testid="custom-order-section">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white border-2 border-[#A85A43] rounded-lg p-8 md:p-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-8 h-8 text-[#A85A43]" />
              <h2 className="text-3xl sm:text-4xl font-[Playfair_Display] text-[#2A2626] tracking-tight">
                Custom Order
              </h2>
            </div>
            <p className="text-[#5C5855] leading-relaxed mb-8">
              Have something specific in mind? Tell us about your dream hat and we'll create it just for you!
            </p>
            
            <form onSubmit={handleCustomOrderSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-[#2A2626] font-[Manrope] font-semibold mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-[#EADCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#A85A43]"
                  placeholder="Your name"
                  data-testid="custom-order-name-input"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-[#2A2626] font-[Manrope] font-semibold mb-2">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-[#EADCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#A85A43]"
                  placeholder="your@email.com"
                  data-testid="custom-order-email-input"
                />
              </div>
              
              <div>
                <label htmlFor="hatType" className="block text-[#2A2626] font-[Manrope] font-semibold mb-2">
                  Hat Type
                </label>
                <select
                  id="hatType"
                  name="hatType"
                  value={formData.hatType}
                  onChange={(e) => setFormData({ ...formData, hatType: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-[#EADCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#A85A43] bg-white"
                  data-testid="custom-order-hat-type-select"
                >
                  <option value="">Select a style...</option>
                  <option value="beanie">Beanie</option>
                  <option value="dreads-hat">Dreads Hat</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="description" className="block text-[#2A2626] font-[Manrope] font-semibold mb-2">
                  Describe Your Vision
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-[#EADCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#A85A43] resize-none"
                  placeholder="Tell us about colors, patterns, size, or any special requests..."
                  data-testid="custom-order-description-textarea"
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#A85A43] hover:bg-[#8C4935] disabled:bg-[#A85A43]/50 text-white font-[Manrope] py-4 px-8 rounded-full transition-colors"
                data-testid="custom-order-submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Submit Custom Order'}
              </button>
              
              <p className="text-sm text-[#5C5855] text-center mt-4">
                We'll respond to your request at <span className="font-semibold">info@protectmycrown.com</span>
              </p>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
